import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDate(value) {
  if (!value) return '–';
  return new Date(value).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function sourceLabel(value) {
  return ({ live_chat: 'Live-Chat', website_contact: 'Kontaktformular' })[value] || value || 'Unbekannt';
}

function statusLabel(lead) {
  if (lead.status === 'unsubscribed') return 'Abgemeldet';
  if (lead.status === 'bounced') return 'Nicht zustellbar';
  if (lead.status === 'suppressed') return 'Gesperrt';
  if (lead.marketing_eligible) return 'Werbung erlaubt';
  return 'Nur Kontaktanfrage';
}

export default function MarketingLeadOverview({ token, active }) {
  const [overview, setOverview] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [consent, setConsent] = useState('all');
  const [conversion, setConversion] = useState('all');

  const load = useCallback(async () => {
    if (!token) return;
    setLoaded(true);
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/admin/marketing-leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Lead-Übersicht konnte nicht geladen werden.');
      setOverview(data);
    } catch (loadError) {
      setError(loadError.message || 'Lead-Übersicht konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (active && token && !loaded) load();
  }, [active, token, loaded, load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (overview.items || []).filter((lead) => {
      if (source !== 'all' && lead.source !== source) return false;
      if (consent === 'eligible' && !lead.marketing_eligible) return false;
      if (consent === 'none' && lead.marketing_consent) return false;
      if (consent === 'blocked' && !['unsubscribed', 'bounced', 'suppressed'].includes(lead.status)) return false;
      if (conversion === 'converted' && !lead.converted) return false;
      if (conversion === 'open' && lead.converted) return false;
      if (!needle) return true;
      return [lead.name, lead.email, lead.phone, lead.customer?.company_name]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [overview.items, search, source, consent, conversion]);

  return (
    <section hidden={!active} className="card admin-section-card admin-section-anchor" aria-labelledby="marketing-leads-title">
      <div className="admin-section-header">
        <div>
          <h2 id="marketing-leads-title">Kontakte & Leads</h2>
          <p className="subtitle">Anfragen und Werbeeinwilligungen werden getrennt dargestellt. Diese Ansicht versendet keine E-Mails.</p>
        </div>
        <button type="button" className="secondary-button" onClick={load} disabled={loading}>
          {loading ? 'Wird geladen …' : 'Aktualisieren'}
        </button>
      </div>
      {error ? <div className="error-message" role="alert">{error}</div> : null}
      <div className="lead-summary" aria-label="Lead-Zusammenfassung">
        <div><strong>{overview.counts?.total || 0}</strong><span>Kontakte</span></div>
        <div><strong>{overview.counts?.not_converted || 0}</strong><span>Noch kein Kunde</span></div>
        <div><strong>{overview.counts?.converted || 0}</strong><span>Konvertiert</span></div>
        <div><strong>{overview.counts?.marketing_eligible_not_converted || 0}</strong><span>Kontaktierbar, nicht konvertiert</span></div>
      </div>
      <div className="lead-filters">
        <label><span>Suche</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, E-Mail, Telefon oder Firma" /></label>
        <label><span>Quelle</span><select value={source} onChange={(event) => setSource(event.target.value)}><option value="all">Alle</option><option value="live_chat">Live-Chat</option><option value="website_contact">Kontaktformular</option></select></label>
        <label><span>Einwilligung</span><select value={consent} onChange={(event) => setConsent(event.target.value)}><option value="all">Alle</option><option value="eligible">Werbung erlaubt</option><option value="none">Keine Einwilligung</option><option value="blocked">Abgemeldet / gesperrt</option></select></label>
        <label><span>Conversion</span><select value={conversion} onChange={(event) => setConversion(event.target.value)}><option value="all">Alle</option><option value="open">Noch kein Kunde</option><option value="converted">Konvertiert</option></select></label>
      </div>
      {!loading && !visible.length ? <p>Für diese Auswahl sind keine Kontakte vorhanden.</p> : null}
      <div className="lead-list">
        {visible.map((lead) => (
          <article className="lead-card" key={lead.id}>
            <div className="lead-card__header"><div><strong>{lead.name || lead.email}</strong>{lead.name ? <span>{lead.email}</span> : null}</div><span className={`lead-state ${lead.marketing_eligible ? 'lead-state--allowed' : ''}`}>{statusLabel(lead)}</span></div>
            <div className="lead-card__details">
              <span>Quelle: <strong>{sourceLabel(lead.source)}</strong></span>
              <span>Letzte Anfrage: <strong>{formatDate(lead.last_inquiry_at || lead.created_at)}</strong></span>
              <span>Anfragen: <strong>{lead.inquiry_count || 1}</strong></span>
              <span>Kundenstatus: <strong>{lead.converted ? (lead.customer?.company_name || 'Konvertiert') : 'Noch kein Kunde'}</strong></span>
              {lead.phone ? <span>Telefon: <strong>{lead.phone}</strong></span> : null}
              {lead.suppression_reason ? <span>Sperrgrund: <strong>{lead.suppression_reason}</strong></span> : null}
            </div>
          </article>
        ))}
      </div>
      {overview.truncated ? <p className="subtitle">Es werden die 500 zuletzt aktiven Kontakte angezeigt.</p> : null}
      <style>{`
        .lead-summary { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin:18px 0; }
        .lead-summary div { padding:14px; border:1px solid #dce4ed; border-radius:10px; background:#f7f9fc; }
        .lead-summary strong,.lead-summary span { display:block; } .lead-summary strong { font-size:1.45rem; }
        .lead-summary span { margin-top:3px; color:#617087; font-size:.78rem; }
        .lead-filters { display:grid; grid-template-columns:2fr repeat(3,1fr); gap:12px; margin-bottom:18px; }
        .lead-filters label { display:grid; gap:5px; } .lead-filters label>span { font-size:.75rem; font-weight:700; color:#526076; }
        .lead-filters input,.lead-filters select { width:100%; padding:10px; border:1px solid #cbd6e2; border-radius:8px; background:white; }
        .lead-list { display:grid; gap:10px; } .lead-card { padding:14px; border:1px solid #dce4ed; border-radius:10px; }
        .lead-card__header { display:flex; justify-content:space-between; gap:12px; align-items:flex-start; }
        .lead-card__header div span { display:block; margin-top:3px; color:#617087; font-size:.8rem; }
        .lead-state { padding:4px 8px; border-radius:999px; background:#eef1f5; color:#526076; font-size:.72rem; font-weight:700; }
        .lead-state--allowed { background:#dcf7e8; color:#17643a; }
        .lead-card__details { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px 14px; margin-top:12px; font-size:.78rem; color:#617087; }
        @media(max-width:980px){.lead-summary,.lead-filters,.lead-card__details{grid-template-columns:1fr 1fr;}}
        @media(max-width:620px){.lead-summary,.lead-filters,.lead-card__details{grid-template-columns:1fr}.lead-card__header{flex-direction:column}}
      `}</style>
    </section>
  );
}
